/**
 * Custom-framework taxonomy import hooks (P260).
 *
 * Stage (upload a CSV) -> review the dry-run -> commit. Mirrors the
 * foundation import hooks but points at the framework endpoints. The
 * upload posts multipart/form-data, so it bypasses `api.post` (which sets
 * a JSON content-type) and uses `fetch` directly with the bearer token.
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { api, currentAuthToken } from "shared/api/client";
import type { FrameworkImportBatch } from "shared/api/types";

const STAGE_URL = "/v1/admin/framework/frameworks/import";

export class FrameworkImportError extends Error {
  constructor(
    public readonly status: number,
    detail: string,
  ) {
    super(detail);
    this.name = "FrameworkImportError";
  }
}

async function _upload(file: File): Promise<FrameworkImportBatch> {
  const token = currentAuthToken();
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(STAGE_URL, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      // keep statusText
    }
    throw new FrameworkImportError(response.status, detail);
  }
  return (await response.json()) as FrameworkImportBatch;
}

export function useUploadFrameworkCsv(): UseMutationResult<
  FrameworkImportBatch,
  FrameworkImportError,
  File
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: _upload,
  });
}

export function useCommitFrameworkBatch(): UseMutationResult<
  FrameworkImportBatch,
  Error,
  string
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (batchId: string) =>
      api.post<FrameworkImportBatch>(`${STAGE_URL}/batches/${batchId}/commit`),
  });
}

export async function downloadFrameworkTemplate(): Promise<void> {
  const token = currentAuthToken();
  const response = await fetch(`${STAGE_URL}/template.csv`, {
    method: "GET",
    headers: {
      Accept: "text/csv",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    throw new FrameworkImportError(
      response.status,
      response.statusText || `HTTP ${response.status}`,
    );
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-framework-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
